import { useQuery, useQueryClient } from '@tanstack/react-query'
import { compact, partition, sortBy, uniqBy } from 'lodash'
import moment from 'moment/moment'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Booking } from '../../models/Booking'
import { ComputeFactory } from '../../models/ComputeFactory'
import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { CancellationModal } from '../common/CancellationModal'
import { HeaderButton, BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { Select } from '../common/fields/Select'

const viewWillAppear = async ({ urlParams }: PocketComponent) => {
    const lessonID = urlParams.id
    if (!lessonID) {
        return new RecordNotFoundError('Class not found')
    }

    await RecordFactory.getInstance().fetchLesson(lessonID)

    const lesson = await RecordFactory.getInstance().loadClass(lessonID)
    if (!lesson) {
        return new RecordNotFoundError('Class not found')
    }
    if (!lesson.item) {
        return new RecordNotFoundError('Class not found')
    }

    return { lesson, item: lesson.item }
}

const render = ({
    data,
    base: { logAndDisplayError, me },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { lesson, item } = data

    usePageTitle(item.name)

    const { findLastHistoryEntry } = useHistoryTrackerContext()
    const navigate = useNavigate()

    const backHistoryEntry = findLastHistoryEntry({
        excluding: /^\/settings\/classes.*\/(new|edit)/,
    })

    const { data: bookings } = useQuery({
        queryKey: ['lessons', lesson.id],
        queryFn: () => ComputeFactory.getInstance().fetchClassBookings(lesson),
    })
    const { data: numberOfBookings } = useQuery({
        queryKey: ['lessons', lesson.id, 'numberOfBookings'],
        queryFn: () => lesson.numberOfBookings(),
    })
    const queryClient = useQueryClient()

    const [bookingPendingCancellation, setBookingPendingCancellation] = useState<Booking | null>(
        null
    )
    const [isBookingPendingCancellationLoading, setIsBookingPendingCancellationLoading] =
        useState(false)

    const [visibleModalType, setVisibleModalType] = useState<
        'dropin' | 'waitlist' | 'cancel' | null
    >(null)

    const [cancellationModalBooking, setCancellationModalBooking] = useState<Booking | null>(null)

    const classTypeLabel =
        Item.classTypeStrings().find((option) => option.id === item.classType)?.label ?? 'TBD'

    const [confirmedBookings, waitlistBookings] = partition(
        bookings ?? [],
        (booking) => booking.status !== 'waitListed'
    )

    const { data: team } = useQuery({
        queryKey: ['lessons', lesson.id, 'team'],
        queryFn: () =>
            ComputeFactory.getInstance().fetchTeamByPeople(
                uniqBy(
                    compact([
                        ...confirmedBookings.map((booking) => booking.client),
                        item.employee,
                        me,
                    ]),
                    (user) => user.id ?? ''
                )
            ),
    })

    const scheduleDropin = () => {
        setVisibleModalType(null)
        navigate(`/bookings/new?lessonId=${encodeURIComponent(lesson.id ?? '')}&dropin=true`)
    }

    const scheduleClasses = async () => {
        setVisibleModalType(null)
        navigate(`/bookings/new?lessonId=${encodeURIComponent(lesson.id ?? '')}`)
    }

    const handleBook = () => {
        if (numberOfBookings === undefined) {
            return
        }

        if (!item.dropin) {
            setVisibleModalType('dropin')
            return
        }

        if (item.allowWaitlist && numberOfBookings >= (lesson.capacity ?? 0)) {
            setVisibleModalType('waitlist')
            return
        }

        scheduleDropin()
    }

    const headerButtons = (() => {
        const buttons: HeaderButton[] = []

        if (
            !!lesson.confirmedDate &&
            !lesson.cancelledDate &&
            !lesson.completedDate &&
            numberOfBookings !== undefined
        ) {
            buttons.push({
                title: 'Book',
                onClick: handleBook,
            })
        }

        if (team?.id !== undefined) {
            buttons.push({
                title: 'Message',
                onClick: () => navigate(`/messages/${team.id}`, { state: { bcc: true } }),
            })
        }

        if (!lesson.cancelledDate) {
            buttons.push({
                title: 'Cancel',
                onClick: () => setVisibleModalType('cancel'),
            })
        }

        buttons.push({
            title: 'Edit',
            onClick: () => navigate(`/settings/classes/${item.id}/lessons`),
        })

        return buttons
    })()

    const handleSelectRosterOption = async (optionID: string, booking: Booking) => {
        if (optionID === 'check-in') {
            await RecordFactory.getInstance().checkinBooking(booking)
            queryClient.invalidateQueries({ queryKey: ['lessons', lesson.id] })
        }
        else if (optionID === 'cancel') {
            if (booking.confirmedDate) {
                setCancellationModalBooking(booking)
            }
            else {
                setBookingPendingCancellation(booking)
            }
        }
        else if (optionID === 'promote') {
            await RecordFactory.getInstance().promoteBooking(booking)
            queryClient.invalidateQueries({ queryKey: ['lessons', lesson.id] })
        }
    }

    const handleCancelBooking = async (booking: Booking, shouldNotify: boolean) => {
        setIsBookingPendingCancellationLoading(true)
        await RecordFactory.getInstance().cancelBooking(booking, !shouldNotify, false)
        queryClient.invalidateQueries({ queryKey: ['lessons', lesson.id] })
        setIsBookingPendingCancellationLoading(false)
        setBookingPendingCancellation(null)
    }

    const handleCancelClass = async (shouldNotify: boolean, shouldCancelAll: boolean) => {
        setVisibleModalType(null)

        const result = await RecordFactory.getInstance().cancelClass(
            lesson,
            shouldNotify,
            shouldCancelAll
        )

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        navigate(backHistoryEntry?.pathname ?? '/dashboards/classes')
    }

    const handleCancellationComplete = () => {
        setCancellationModalBooking(null)
        queryClient.invalidateQueries({ queryKey: ['lessons', lesson.id] })
    }

    const hasFutureClasses = !!lesson.schedule && moment(lesson.date) > moment()

    return (
        <BaseRecordView
            title="Class"
            headerButtons={headerButtons}
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : {
                          title: 'Classes',
                          onClick: () => navigate('/dashboards/classes'),
                      }
            }
        >
            <BaseRecordView.Container>
                <BaseRecordView.LeftColumn>
                    <Card size="sm">
                        <div className="flex flex-col items-center justify-center">
                            {item?.image?.uri !== undefined ? (
                                <img
                                    src={item.image.uri}
                                    className="size-[116px] rounded-full object-cover"
                                />
                            ) : (
                                <Icon
                                    icon="student.large"
                                    background="round"
                                    className="size-[116px]"
                                    svgClassName="size-[56px]"
                                />
                            )}

                            <Heading size="sm" className="mt-6 mb-1 text-center">
                                {item.name ?? ''}
                            </Heading>

                            <div className="flex flex-col items-center justify-center gap-y-1">
                                <Paragraph size="lg" className="font-normal text-gray-600">
                                    {moment(lesson.date).format('ddd, MMM Do YYYY')}
                                </Paragraph>
                                <Paragraph size="lg" className="font-normal text-gray-600">
                                    {moment(lesson.date).format('h:mm A')} -{' '}
                                    {moment(lesson.endDate).format('h:mm A')}
                                </Paragraph>
                            </div>
                        </div>

                        <Divider className="my-6" />

                        <div className="flex flex-col items-stretch justify-start gap-y-6">
                            <BaseRecordView.IconField
                                icon="users.large"
                                title={lesson.capacity?.toString() ?? ''}
                                subtitle="Class capacity"
                            />

                            <BaseRecordView.IconField
                                icon="dollar.large"
                                title={
                                    item.rate === undefined
                                        ? 'TBD'
                                        : item.rate === 0
                                          ? 'Free'
                                          : currencyFormat(item.rate)
                                }
                                subtitle="Price"
                            />

                            <BaseRecordView.IconField
                                icon="userPlus.large"
                                title={classTypeLabel}
                                subtitle="Type"
                            />

                            {lesson.schedule && (
                                <BaseRecordView.IconField
                                    icon="calendar.large"
                                    title={lesson.schedule.description}
                                    subtitle="Schedule"
                                />
                            )}

                            {lesson.employee && lesson.employee.id && (
                                <BaseRecordView.IconField
                                    icon={<ContactAvatar user={lesson.employee} hasHoverEffect />}
                                    title={lesson.employee.name ?? ''}
                                    subtitle="Instructor"
                                    href={`/team-members/${lesson.employee.id}`}
                                />
                            )}
                        </div>
                    </Card>

                    {!!lesson.location && <BaseRecordView.LocationSection location={lesson.location} />}
                </BaseRecordView.LeftColumn>

                <BaseRecordView.RightColumn>
                    {confirmedBookings.length > 0 && (
                        <Card
                            className="flex flex-col items-stretch justify-start gap-y-8"
                            title="Roster"
                            count={
                                confirmedBookings.length > 0 ? confirmedBookings.length : undefined
                            }
                        >
                            <div className="flex flex-col items-stretch justify-center gap-y-8">
                                {sortBy(
                                    confirmedBookings,
                                    (booking) => booking.client?.name?.toLowerCase() ?? ''
                                ).map((booking) => (
                                    <Link
                                        key={booking.id ?? ''}
                                        to={`/bookings/${booking.id}`}
                                        className="group relative"
                                    >
                                        <div className="absolute -inset-4 z-10 rounded-lg group-hover:bg-black/4" />

                                        <div className="relative z-20 flex items-center justify-center gap-x-4">
                                            {booking.client !== undefined ? (
                                                <ContactAvatar
                                                    user={booking.subaccount ?? booking.client}
                                                    hasHoverEffect
                                                />
                                            ) : (
                                                <Icon icon="user.large" />
                                            )}

                                            <div className="flex grow flex-col items-stretch justify-center gap-y-1">
                                                <Label size="md" className="font-bold">
                                                    {booking.subaccount?.name ??
                                                        booking.client?.name}

                                                    {booking.subaccount !== undefined && (
                                                        <Label
                                                            size="md"
                                                            className="ml-1 font-light text-gray-500"
                                                        >
                                                            {booking.client?.name}
                                                        </Label>
                                                    )}
                                                </Label>
                                                <Paragraph size="sm" className="text-gray-600">
                                                    {booking.checkinDate !== undefined
                                                        ? `Checked in at ${moment(booking.checkinDate).format('h:mm A')} on ${moment(booking.checkinDate).format('MMM Do YYYY')}` +
                                                          (booking.checkinBy !== undefined
                                                              ? ` by ${booking.checkinBy.name}`
                                                              : '')
                                                        : (booking.subaccount?.labelString ??
                                                          'Client')}
                                                </Paragraph>
                                            </div>

                                            <Select
                                                trigger={
                                                    <button className="flex size-12 items-center justify-center rounded-lg hover:bg-black/4">
                                                        <Icon
                                                            icon="moreHorizontal.medium"
                                                            className="text-gray-500"
                                                        />
                                                    </button>
                                                }
                                                options={compact([
                                                    booking.checkinDate === undefined && {
                                                        id: 'check-in',
                                                        title: 'Check in',
                                                        icon: 'calendarArrowLeft',
                                                    },
                                                    {
                                                        id: 'cancel',
                                                        title: 'Cancel',
                                                        isDanger: true,
                                                    },
                                                ])}
                                                onSelectOption={(option) =>
                                                    handleSelectRosterOption(option.id, booking)
                                                }
                                                align="end"
                                            />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </Card>
                    )}

                    {waitlistBookings.length > 0 && (
                        <Card
                            className="flex flex-col items-stretch justify-start gap-y-8"
                            title="Wait list"
                            count={
                                waitlistBookings.length > 0 ? waitlistBookings.length : undefined
                            }
                        >
                            {waitlistBookings.length > 0 && (
                                <div className="flex flex-col items-stretch justify-center gap-y-8">
                                    {sortBy(
                                        waitlistBookings,
                                        (booking) => booking.client?.name?.toLowerCase() ?? ''
                                    ).map((booking) => (
                                        <a
                                            key={booking.id ?? ''}
                                            href={`/bookings/${booking.id}`}
                                            className="group relative"
                                        >
                                            <div className="absolute -inset-4 z-10 rounded-lg group-hover:bg-black/4" />

                                            <div className="relative z-20 flex items-center justify-center gap-x-4">
                                                {booking.client !== undefined ? (
                                                    <ContactAvatar
                                                        user={booking.client}
                                                        hasHoverEffect
                                                    />
                                                ) : (
                                                    <Icon icon="user.large" />
                                                )}

                                                <div className="flex grow flex-col items-stretch justify-center gap-y-1">
                                                    <Label size="md" className="font-bold">
                                                        <a
                                                            href={`/clients/${booking.client?.id}`}
                                                            className="hover:text-gray-700 hover:underline"
                                                        >
                                                            {booking.client?.name}
                                                        </a>
                                                    </Label>
                                                    <Paragraph size="sm" className="text-gray-600">
                                                        Client
                                                    </Paragraph>
                                                </div>

                                                <Select
                                                    trigger={
                                                        <button className="flex size-12 items-center justify-center rounded-lg hover:bg-black/4">
                                                            <Icon
                                                                icon="moreHorizontal.medium"
                                                                className="text-gray-500"
                                                            />
                                                        </button>
                                                    }
                                                    options={compact([
                                                        booking.checkinDate === undefined && {
                                                            id: 'promote',
                                                            title: 'Promote',
                                                        },
                                                        {
                                                            id: 'cancel',
                                                            title: 'Cancel',
                                                            isDanger: true,
                                                        },
                                                    ])}
                                                    onSelectOption={(option) =>
                                                        handleSelectRosterOption(option.id, booking)
                                                    }
                                                    align="end"
                                                />
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </Card>
                    )}

                    <Card>
                        <Heading size="sm" className="text-gray-700">
                            Description
                        </Heading>

                        <Paragraph size="md" className="mt-8 text-gray-600">
                            {item.memo}
                        </Paragraph>
                    </Card>
                </BaseRecordView.RightColumn>
            </BaseRecordView.Container>

            {bookingPendingCancellation && (
                <Modal
                    isOpen
                    onClose={() => setBookingPendingCancellation(null)}
                    size="medium"
                    title="Cancel appointment"
                    footer={
                        <div className="flex items-center justify-end gap-x-4">
                            <Button
                                intent="destructive"
                                onClick={() =>
                                    handleCancelBooking(bookingPendingCancellation, false)
                                }
                                isLoading={isBookingPendingCancellationLoading}
                            >
                                Cancel
                            </Button>
                            {!!bookingPendingCancellation.channel && (
                                <Button
                                    intent="destructive"
                                    onClick={() =>
                                        handleCancelBooking(bookingPendingCancellation, true)
                                    }
                                    isLoading={isBookingPendingCancellationLoading}
                                >
                                    Cancel & Notify
                                </Button>
                            )}
                        </div>
                    }
                >
                    Are you sure you want to cancel this appointment?
                </Modal>
            )}

            <Modal
                isOpen={visibleModalType === 'waitlist'}
                onClose={() => setVisibleModalType(null)}
                size="medium"
                title="Add to wait list"
                footer={<Button onClick={scheduleDropin}>Continue</Button>}
            >
                <Paragraph size="md" className="text-gray-600">
                    This class is currently full. You can add this client to the Wait List for this
                    class. If space becomes available up to 12 hours before the class, the client
                    will be automatically booked into the class.
                </Paragraph>
            </Modal>

            <Modal
                isOpen={visibleModalType === 'dropin'}
                onClose={() => setVisibleModalType(null)}
                size="medium"
                title="Schedule class"
                footer={
                    <div className="flex items-center justify-end gap-x-4">
                        <Button onClick={scheduleDropin}>Schedule dropin</Button>
                        <Button onClick={scheduleClasses}>Schedule classes</Button>
                    </div>
                }
            >
                <Paragraph size="md" className="text-gray-600">
                    To schedule the class as a one time dropin choose 'Schedule dropin' to schedule
                    all future classes in series choose 'Schedule classes'
                </Paragraph>
            </Modal>

            <Modal
                isOpen={visibleModalType === 'cancel'}
                onClose={() => setVisibleModalType(null)}
                size="medium"
                title="Cancel class?"
                showDividers={false}
                footer={
                    <div className="flex items-center justify-end gap-x-4">
                        {hasFutureClasses ? (
                            <>
                                <Button
                                    onClick={() => handleCancelClass(false, false)}
                                    intent="tertiary_deprecated"
                                >
                                    Cancel this class
                                </Button>
                                <Button
                                    onClick={() => handleCancelClass(false, true)}
                                    intent="primary"
                                >
                                    Cancel this and future lessons
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button onClick={() => handleCancelClass(true, false)}>
                                    Cancel & notify
                                </Button>
                                <Button onClick={() => handleCancelClass(false, false)}>
                                    Cancel class
                                </Button>
                            </>
                        )}
                    </div>
                }
            >
                {hasFutureClasses ? (
                    <>
                        This action cannot be undone. Select "Cancel this class" to cancel this
                        class lesson, otherwise select "Cancel this and future lessons" to cancel
                        this and all future lessons tied to this class.
                    </>
                ) : (
                    <>
                        This action cannot be undone. Select "Cancel & notify" to notify the client
                        of the cancellation, otherwise select "Cancel class".
                    </>
                )}
            </Modal>

            {cancellationModalBooking !== null && cancellationModalBooking.vendor && (
                <CancellationModal
                    isOpen
                    onClose={() => setCancellationModalBooking(null)}
                    target={{
                        type: 'booking',
                        booking: cancellationModalBooking,
                        doNotCancelAll: true,
                    }}
                    vendor={cancellationModalBooking.vendor}
                    me={me}
                    onCancellationComplete={handleCancellationComplete}
                />
            )}
        </BaseRecordView>
    )
}

export const LessonPage = usePSComponent(render, viewWillAppear)
